import {
  Box,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { ControllerRenderProps } from 'react-hook-form';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { convertToTestIdFormat } from '../../utils/stringUtils';
import { mergeSx } from '@/shared/utils/merge-sx.util';

type RadioInputBox = {
  id: string;
  text: string;
  subText?: string;
  value: string;
  tooltip?: React.ReactNode;
  disabled?: boolean;
};

export interface GroupRadioButtonProps {
  dataTestId: string;
  id: string;
  options: RadioInputBox[];
  field: ControllerRenderProps<any, any>;
  disabled?: boolean;
  containerSx?: SxProps<Theme>;
  itemSx?: SxProps<Theme>;
}

const GroupRadioButton = ({
  id,
  dataTestId,
  options,
  field,
  disabled,
  containerSx,
  itemSx,
}: GroupRadioButtonProps) => {
  const theme = useTheme();
  return (
    <>
      <RadioGroup
        data-testid={dataTestId}
        id={id}
        {...field}
        value={field.value || ''}
        aria-labelledby={`${id}Header`}
        sx={mergeSx(containerSx, { gap: 1.5 })}
      >
        {options.map((item) => {
          const isDisabled = item.disabled || disabled;
          return (
            <Box
              key={item.id}
              sx={mergeSx(itemSx, {
                border: '1px solid lightgrey',
                borderRadius: '4px',
                p: 2,
                textAlign: 'left',
                backgroundColor: isDisabled
                  ? theme.palette.action.disabledBackground
                  : field.value === item.value
                    ? theme.palette.secondary.light
                    : null,
                borderColor:
                  field.value === item.value && !isDisabled ? theme.palette.primary.main : null,
              })}
            >
              <FormControlLabel
                sx={{
                  width: '100%',
                  '& .MuiFormControlLabel-label': {
                    width: '100%',
                  },
                }}
                disabled={isDisabled}
                key={item.value}
                id={convertToTestIdFormat(item.value)}
                value={item.value}
                control={<Radio id={'id_' + convertToTestIdFormat(item.value)} />}
                label={
                  <Stack
                    sx={{ color: 'green' }}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box display="flex" flexDirection="column">
                      <Typography variant="body1">{item.text}</Typography>
                      {item.subText && <Typography variant="helpertext">{item.subText}</Typography>}
                    </Box>
                    {item.tooltip && (
                      <Tooltip
                        title={item.tooltip}
                        placement="right"
                        componentsProps={{ tooltip: { sx: { width: '220px' } } }}
                        enterTouchDelay={0}
                      >
                        <IconButton>
                          <InfoOutlinedIcon sx={{ color: 'grey' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                }
              />
            </Box>
          );
        })}
      </RadioGroup>
    </>
  );
};

export const GroupRadioButtonSkeleton = ({
  count = 3,
  dataTestId,
  containerSx,
  itemSx,
}: {
  count?: number;
  dataTestId?: string;
  containerSx?: SxProps<Theme>;
  itemSx?: SxProps<Theme>;
}) => (
  <Stack data-testid={dataTestId} direction="column" gap={1.5} sx={containerSx}>
    {Array.from({ length: count }, (_, i) => (
      <Box
        key={i}
        sx={mergeSx(itemSx, {
          border: '1px solid lightgrey',
          borderRadius: '4px',
          py: 2,
          px: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        })}
      >
        <Box display="flex" alignItems="center" justifyContent="center" width="42px" height="42px">
          <Skeleton variant="circular" width={24} height={24} sx={{ flexShrink: 0 }} />
        </Box>
        <Skeleton variant="text" width="40%" height={24} />
      </Box>
    ))}
  </Stack>
);

export default GroupRadioButton;
